/**
 * Shared helpers for outbound campaign customer bulk selection / delete (form + table).
 */

export const newClientRowKey = () =>
  `local-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** Unique key per row: server id, or temporary key for unsaved rows. */
export function getRowKey(customer) {
  if (customer?.id != null && customer.id !== '') return String(customer.id);
  return customer?.clientRowKey || '';
}

/** Ensures unsaved rows have a clientRowKey so they can be selected and removed locally. */
export function ensureClientRowKey(customer) {
  return {
    ...customer,
    manual: customer.manual || false,
    caller_opted_out: !!customer.caller_opted_out,
    ...(!customer.id ? { clientRowKey: customer.clientRowKey || newClientRowKey() } : {}),
  };
}

/** Active members cannot be removed via bulk (same as single-row delete). */
export function canBulkDeleteContact(customer) {
  return customer?.status !== 'active';
}

/** Edit Campaign: bulk delete may include active contacts (row delete is not shown). */
export function canSelectContactForBulkInEditMode(customer) {
  return !!getRowKey(customer);
}

/** Reads deleted count and whether any removal happened from the bulk-delete API response. */
export function readBulkDeleteResponse(response, requestedIdCount) {
  const deletedCount =
    response?.data?.deleted_count ??
    response?.deleted_count ??
    (response?.status ? requestedIdCount : 0);
  const removed = !!(response?.status || deletedCount > 0);
  return { deletedCount, removed };
}

/**
 * Returns a new list without rows that were deleted on the server (saved ids) and/or only in memory (local keys).
 */
export function removeContactsFromList(
  customers,
  { savedIds = [], localKeys = [] },
  keyFn = getRowKey
) {
  const savedIdSet = new Set(savedIds.map(Number));
  const localKeySet = localKeys instanceof Set ? localKeys : new Set(localKeys);
  return customers.filter((customer) => {
    if (customer.id && savedIdSet.has(Number(customer.id))) return false;
    if (!customer.id && localKeySet.has(keyFn(customer))) return false;
    return true;
  });
}
